import { useMemo } from 'react';
import { tv } from 'tailwind-variants';

import { TokenContentRelatedTokensType } from '@/lib/api/tokens/types';

import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { fontStyles } from '@/styles/typography';

import { ArrowExternalIcon } from '@/ui/Icons/ArrowExternalIcon';
import { CircleLogoIcon } from '@/ui/Icons/CircleLogoIcon';

import { TokenProfileRelatedTokenShortcut } from '../TokenShortcut';
import { TokenProfileRelatedTokenShortcutSummary } from '../TokenShortcutSummary';

const linkContainerStyles = tv({
  base: [
    'grid',
    'border-l',
    'border-l-token-gray-200',
    'border-solid',
    'gap-2',
    'pt-3',
    'pb-2',
    'ml-5',
    'pl-3',
    'tablet:[grid-template-columns:repeat(auto-fit,_220px)]',
    'grid-cols-1',
  ],
  variants: {
    layout: {
      COLUMN: 'tablet:[grid-template-columns:auto]',
      GRID: '',
    },
  },
});

type EnrichedTokenContentRelatedTokensType = TokenContentRelatedTokensType & {
  symbol?: string;
};

type TokenProfileRelatedTokensProps = {
  relatedTokens: EnrichedTokenContentRelatedTokensType[];
  loading?: boolean;
  issuer?: string;
  showSummary: boolean;
  content: string;
};

export function TokenProfileRelatedTokens({
  relatedTokens,
  loading,
  issuer,
  showSummary,
  content,
}: TokenProfileRelatedTokensProps) {
  const sectionTitleIcon = useMemo(() => {
    if (issuer === 'circle') {
      return <CircleLogoIcon className="size-7" />;
    } else {
      return <ArrowExternalIcon />;
    }
  }, [issuer]);

  return (
    <div className="flex flex-col px-6 tablet:px-8 pt-6 pb-6 tablet:pb-8">
      <h2 className={fontStyles({ variant: 'H5', class: ['mb-6', 'block'] })}>Related Tokens</h2>
      <p className={fontStyles({ variant: 'BODY_LG', class: ['text-token-gray-800', 'mb-6'] })}>
        {content}
      </p>
      <div>
        <div className="flex items-center gap-2">
          <div
            className={`bg-token-gray-200 border-token-gray-500 p-1.5 rounded-full border-solid border w-fit h-fit flex items-center justify-center ${!(issuer === 'circle') ? 'ml-2' : ''}`}
          >
            {sectionTitleIcon}
          </div>
          <span className={fontStyles({ variant: 'LABEL_MD' })}>
            {relatedTokens[0]?.category?.sectionTitle}
          </span>
        </div>
        <div className={linkContainerStyles({ layout: showSummary ? 'COLUMN' : 'GRID' })}>
          {loading
            ? new Array(3)
                .fill(null)
                .map((_, index) => (
                  <TokenProfileRelatedTokenShortcut loading={loading} key={index} />
                ))
            : relatedTokens.map(({ chainId, address, symbol, summary }, index) =>
                showSummary ? (
                  <TokenProfileRelatedTokenShortcutSummary
                    loading={loading}
                    key={index}
                    title={symbol || 'Unknown Token'}
                    summary={summary}
                    subtitle={`on ${NETWORK_NAME_PER_CHAIN_ID[chainId]}`}
                    chainId={chainId}
                    tokenAddress={address}
                  />
                ) : (
                  <TokenProfileRelatedTokenShortcut
                    loading={loading}
                    key={index}
                    title={symbol || 'Unknown Token'}
                    subtitle={`on ${NETWORK_NAME_PER_CHAIN_ID[chainId]}`}
                    chainId={chainId}
                    tokenAddress={address}
                  />
                ),
              )}
        </div>
      </div>
    </div>
  );
}
